import aosw from '../../aosw';
import logger from '../../logger';
import {
  clearSecurityQuestionsPromptCookies,
  clearStorage,
  getStateTokenOverride,
  removeStateTokenOverride
} from '../utils/storage-utils';
import rewriteUrlOnChange from '../rewrite-url';
import { isAthenaOneWebClient } from '../utils/app-utils';
import {
  completeInteractionCodeFlow,
  getConfigForInteractionCodeFlow,
  getConfigForStateTokenOverrideFlow,
  onSuccessfulAuth,
  saveIdxTransactionMetadata,
  setAuthClientOptionsForInteractionCodeFlow
} from '../utils/authorize-utils';
import athenaConfig from '../config';
import AmplitudeUtils from '../utils/amplitude-utils';
import { launchedFromLwaProfile, lwaProfileLaunchedParams } from '../utils/lwa-profile-utils';
import { launchedFromAthenanetUserProfile, sendNoticeToParentWindow } from '../utils/athenanet-user-profile-utils';
import { initAppSettings } from '../utils/app-settings';
import '../../aosw.scss';
import { useEffect, useState } from 'react';
import Loader from './common/Loader';

const { oieEnabled } = athenaConfig;

async function launchWidget(setLoading: (loading: boolean) => void) {
  if (launchedFromLwaProfile() || launchedFromAthenanetUserProfile()) {
    global.jQueryCourage('div.back-to-settings').remove();
  }
  jQueryCourage('html').show();
  const { OktaUtil, OktaSignIn } = global;
  // "config" object contains default widget configuration
  // with any custom overrides defined in your admin settings.
  const config = OktaUtil.getSignInWidgetConfig();
  //Clear storage when user cancels the consent too
  const cancel = config.consent?.cancel;
  if (cancel) {
    config.consent.cancel = (...args: any[]) => {
      clearStorage();
      cancel.apply(config, args);
    };
  }

  global.config = config;
  await initAppSettings();
  // Stop the loader after network call instead of waiting for the widget to load as it can cause issues
  // as customizers might target loader's html instead of the actual widget
  setLoading(false);
  try {
    rewriteUrlOnChange();
    aosw.customizeSignInWidget(config);
  }
  catch (e) {
    /* empty */
  }

  // Clear security questions related cookies.
  clearSecurityQuestionsPromptCookies();

  let oktaSignIn;
  const stateToken = getStateTokenOverride();

  //Remove the brandName since we don't need it, and causes problems since widget tries to use parameterized i18n keys
  delete config['brandName'];

  if (isAthenaOneWebClient() && stateToken) {
    AmplitudeUtils.logCustomEvent('StateTokenOverrideFlow', {});
    global.stateTokenOverrideFlow = true;
    removeStateTokenOverride();

    oktaSignIn = new OktaSignIn(getConfigForStateTokenOverrideFlow(config, stateToken));
    oktaSignIn.showSignInAndRedirect().catch((error: any) => logger.log(error.message, error));
  }
  else {
    const { stateHandle, isAuthnWithLwaClientId } = global.settings;
    const isInteractionCodeFlowEnabled = oieEnabled && isAuthnWithLwaClientId && stateHandle;
    const oktaSignInConfig = isInteractionCodeFlowEnabled ? getConfigForInteractionCodeFlow(config) : config;
    oktaSignIn = new OktaSignIn(oktaSignInConfig);
    if (isInteractionCodeFlowEnabled) {
      // saveIdxTransactionMetadata is required to populate transaction meta in
      // https://github.com/okta/okta-signin-widget/blob/master/src/v2/client/startLoginFlow.ts#L53
      // so that we initiate idx.proceed instead of idx.start
      saveIdxTransactionMetadata(oktaSignIn);
      // Auth Client Options should not be set for non interaction code flows.
      setAuthClientOptionsForInteractionCodeFlow(oktaSignIn);
    }
    if (oieEnabled) {
      oktaSignIn.before('success-redirect', async () => {
        await onSuccessfulAuth();
      });
      // redirects the user back to lwaupm app again after successful MFA enrollment post launching from lwaupm app
      oktaSignIn.after('success-redirect', () => {
        if (launchedFromLwaProfile()) {
          const { lwaProfileUrl } = lwaProfileLaunchedParams();
          window.location.href = lwaProfileUrl;
        }
        if (launchedFromAthenanetUserProfile()) {
          // send a message to parent window to close the popup
          sendNoticeToParentWindow();
        }
      });
    }
    oktaSignIn.renderEl(
      { el: '#okta-login-container' },
      // Widget calls this method after successful authentication completion,
      // hook into the same to cleanup the storage
      // to avoid issues with same tab being used for login
      async (res: any) => {
        if (res?.status === 'SUCCESS') {
          if (launchedFromLwaProfile()) {
            const { lwaProfileUrl } = lwaProfileLaunchedParams();
            window.location.href = lwaProfileUrl;
          }
          else if (launchedFromAthenanetUserProfile()) {
            // send a message to parent window to close the popup
            sendNoticeToParentWindow();
          }
          else {
            await onSuccessfulAuth();
            if (isInteractionCodeFlowEnabled) {
              completeInteractionCodeFlow(res);
            }
            else {
              OktaUtil.completeLogin(res);
            }
          }
        }
      },
      (error: any) => logger.log(error.message, error)
    );
  }

  global.oktaSignIn = oktaSignIn;
  try {
    aosw.setupHooks(oktaSignIn);
  }
  catch (e) {
    /* empty */
  }
}

export default function Launcher() {
  const [ loading, setLoading ] = useState(true);
  const [ error, setIsError ] = useState(null);
  useEffect(() => {
    launchWidget(setLoading).catch((err) => {
      setIsError(err);
    });
  }, []);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [ error ]);

  if (loading) {
    return <Loader/>;
  }
  return null;
}
